import {
    AsyncAction,
    CommanderThunkAction,
    CommanderThunkDispatch,
    GetState,
    WithBillId,
    WithScope,
} from '@mews-commander/types';
import { createAction } from '@mews/framework';
import { createFetchAction } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { addBill } from '@mews-commander/legacy-entities/src/actions/bills';
import { deleteBills, getBill } from '@mews-commander/legacy-entities/src/ducks/bills';
import { getOrderItemIdsByBillIds, updateOrderItemsBillId } from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { getPaymentIdsByBillIds, updatePaymentsBillId } from '@mews-commander/legacy-entities/src/ducks/payments';
import { BILL_FETCH_FAILED, BILL_FETCH_STARTED, BILL_FETCH_SUCCEEDED } from '../ducks/bills';
import { prefix } from '../module';
import { replaceExpandedBill } from './billing';

const MOVE_ITEMS = prefix('MOVE_ITEMS');
export const EDIT_ORDER_ITEM = prefix('EDIT_ORDER_ITEM');

interface MoveItemsToTargetBillProps {
    targetBillId: string;
    ownerId: string;
    paymentIds?: string[];
    orderItemsIds?: string[];
    associatedAccountId?: string;
}

export const moveItemsToTargetBill =
    ({
        associatedAccountId,
        targetBillId,
        ownerId,
        paymentIds = [],
        orderItemsIds = [],
    }: MoveItemsToTargetBillProps): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const enterpriseId = getCurrentEnterpriseId();
        const itemIds = [...paymentIds, ...orderItemsIds];

        const promise = repositories.billItems
            .move({
                enterpriseId,
                targetBillId,
                ownerId,
                itemIds,
                associatedAccountId,
            })
            .then(() => {
                dispatch(updatePaymentsBillId({ ids: paymentIds, billId: targetBillId, enterpriseId }));
                dispatch(updateOrderItemsBillId({ ids: orderItemsIds, billId: targetBillId }));
            });

        return dispatch(createFetchAction(MOVE_ITEMS, promise));
    };

export const getRealBillId =
    ({ billId, scope }: WithBillId & WithScope) =>
    async (dispatch: CommanderThunkDispatch, getState: GetState) => {
        const state = getState();

        const enterpriseId = getCurrentEnterpriseId();
        const { isVirtual, ownerId } = getBill(state, { id: billId }) || {};
        if (!isVirtual) {
            return billId;
        }

        if (!ownerId) throw new Error('Bill owner is not defined.');

        dispatch(createAction(BILL_FETCH_STARTED, undefined, { scope }));

        const newState = getState();
        const orderItemsIds = getOrderItemIdsByBillIds(newState, { billIds: [billId] });
        const paymentIds = getPaymentIdsByBillIds(newState, { billIds: [billId], enterpriseId });

        try {
            const { value: newBillId } = await dispatch(addBill({ accountId: ownerId }));
            await dispatch(moveItemsToTargetBill({ targetBillId: newBillId, ownerId, orderItemsIds, paymentIds }));
            dispatch(deleteBills([billId]));
            dispatch(replaceExpandedBill({ fromBillId: billId, toBillId: newBillId, scope }));

            dispatch(createAction(BILL_FETCH_SUCCEEDED, undefined, { scope }));

            return newBillId;
        } catch (error) {
            dispatch(createAction(BILL_FETCH_FAILED, undefined, { scope }));
            throw error;
        }
    };

export const getReallBillIdWithoutDelete =
    ({ billId, scope }: WithBillId & WithScope) =>
    async (dispatch: CommanderThunkDispatch, getState: GetState) => {
        const state = getState();

        const enterpriseId = getCurrentEnterpriseId();
        const { isVirtual, ownerId } = getBill(state, { id: billId }) || {};
        if (!isVirtual) {
            return billId;
        }

        if (!ownerId) throw new Error('Bill owner is not defined.');

        dispatch(createAction(BILL_FETCH_STARTED, undefined, { scope }));

        const newState = getState();
        const orderItemsIds = getOrderItemIdsByBillIds(newState, { billIds: [billId] });
        const paymentIds = getPaymentIdsByBillIds(newState, { billIds: [billId], enterpriseId });

        try {
            const { value: newBillId } = await dispatch(addBill({ accountId: ownerId }));
            await dispatch(moveItemsToTargetBill({ targetBillId: newBillId, ownerId, orderItemsIds, paymentIds }));

            dispatch(createAction(BILL_FETCH_SUCCEEDED, undefined, { scope }));

            return newBillId;
        } catch (error) {
            dispatch(createAction(BILL_FETCH_FAILED, undefined, { scope }));
            throw error;
        }
    };

export const replaceVirtualBill =
    ({ billId, newBillId, scope }: WithBillId & WithScope & { newBillId: string }) =>
    (dispatch: CommanderThunkDispatch) => {
        dispatch(deleteBills([billId]));
        dispatch(replaceExpandedBill({ fromBillId: billId, toBillId: newBillId, scope }));
    };
